<template>
  <slot v-bind="slotProps" />
</template>

<script setup>
import { ref, watch, toRef, computed, onUnmounted } from 'vue'
import { trim } from 'gleam-shared/helpers/nodash'
import debounce from 'gleam-shared/helpers/debounce'

const emit = defineEmits(['confirm', 'input', 'blur', 'focus', 'keydown'])

const props = defineProps({
  /**
   * Defines how long the searchbar should wait after the last input change
   * to emit the changed value. The idea is to avoid overwhelming the listener
   * with search requests, since it's expected for the searching to be expensive.
   */
  delay: {
    type: [String, Number],
    default: 300
  },
  /**
   * Defines the initial request and allows changing the input on the fly.
   */
  request: {
    type: String
  },
  /**
   * Forces the component to emit the current user input whenever it receives focus.
   * Be careful, the component will emit any current value whether it has been emitted before or not.
   */
  emitOnFocus: {
    type: Boolean,
    default: false
  }
})

const userInput = ref(null)
const confirmed = ref(null)
const postFocus = ref(false)

const slotListeners = computed(() => {
  return {
    input: onInput,
    blur: onBlur,
    focus: onFocus,
    keydown: onKeydown
  }
})

const slotProps = computed(() => {
  return {
    clear,
    userInput: userInput.value,
    listeners: slotListeners.value
  }
})

const debouncedConfirm = debounce(confirm, parseFloat(props.delay))

watch(toRef(props, 'request'), (request) => {
  // If "request" is equal to "confirmed", the parent component just received
  // the "confirm" event and updated its state. Otherwise, it changed its state
  // because of a navigation or something, so we must replace what we have with that new value.

  if (trim(request) !== confirmed.value) {
    userInput.value = request
    confirmed.value = trim(request)
    debouncedConfirm.cancel()
  }
}, { immediate: true })

onUnmounted(() => {
  debouncedConfirm.cancel()
})

function confirm() {
  // Emits if the current input has changed or the focus just has been received.

  if ((confirmed.value !== trim(userInput.value)) || postFocus.value) {
    postFocus.value = false
    confirmed.value = trim(userInput.value)
    emit('confirm', confirmed.value)
  }
}

function clear() {
  userInput.value = ''
  processUserInput()
}

function onInput(event) {
  // We don't trim "userInput" here because it's literally the user's input and we don't mess with it.

  userInput.value = event.target.value
  processUserInput()
}

function onBlur() {
  emit('blur')
  immediateConfirm(false)
}

function onFocus() {
  emit('focus')
  immediateConfirm(true)
}

function onKeydown(event) {
  emit('keydown', event)

  if (event.key === 'Enter') {
    immediateConfirm(false)
  }
}

function processUserInput() {
  emit('input', userInput.value)

  if (trim(userInput.value).length === 0) {
    immediateConfirm()
  } else {
    debouncedConfirm()
  }
}

function immediateConfirm(inFocus = false) {
  if (inFocus) {
    if (!props.emitOnFocus) {
      return
    }

    postFocus.value = true
  }

  debouncedConfirm()
  debouncedConfirm.flush()
}
</script>
